import { Typography, Grid, Alert, Button, Box } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { Control, UseFormWatch, UseFormSetValue } from "react-hook-form";
import { useLocation } from 'react-router-dom';
import { useEffect, useState, useRef } from "react";
import { toast } from "react-toastify";
import { Order, PaymentOrderState } from "../../models/order";
import AutocompleteInputOption from "../../models/autocompleteInputOption";
import AppTextInput from "../AppTextInput";
import AppDatePicker from "../AppDatePicker";
import AppSelect from "../AppSelect";
import { OrderFormValues } from "../../models/orderFormValues";
import AppAutocompleteWithDraft from "../AppAutocompleteWithDraft";
import agent from "../../api/agent";

interface Props {
    order: Order | undefined;
    control: Control<OrderFormValues> | undefined;
    possibleServices: AutocompleteInputOption[];
    watch: UseFormWatch<OrderFormValues>;
    setValue: UseFormSetValue<OrderFormValues>;
    handleOpenModal: () => void;
    setManualServiceChange: () => void;
    isAdmin: boolean;
}

export default function OrderFormBasicInformation({ order, control, possibleServices, watch, setValue, handleOpenModal, setManualServiceChange, isAdmin }: Props) {
    const location = useLocation();
    const selectedServiceId = watch('serviceId');
    const customExamName = watch('customeClientServiceName');
    const trainingProgram = watch('trainingProgram');
    const [serviceHasNoTestPath, setServiceHasNoTestPath] = useState<boolean>(false);
    const [selectedService, setSelectedService] = useState<AutocompleteInputOption | undefined>(undefined);
    const [generatingEffects, setGeneratingEffects] = useState<boolean>(false);
    
    // Ref to track if component is mounted (for async operations)
    const isMountedRef = useRef<boolean>(true);
    // Ref for AbortController to cancel ongoing requests
    const abortControllerRef = useRef<AbortController | null>(null);

    // Cleanup on unmount
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            // Cancel any ongoing AI generation request
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    // Check if AI generation should be available
    const serviceName = selectedService?.name || '';
    const canGenerateAIEffects = customExamName && 
        trainingProgram && 
        customExamName.trim() !== '' && 
        trainingProgram.trim() !== '' &&
        customExamName.trim().toLowerCase() !== serviceName.trim().toLowerCase();

    useEffect(() => {
        const service = possibleServices.find(service => service.id === selectedServiceId);
        setSelectedService(service);

        if (service) {
            // Sprawdź typ usługi i odpowiedni atrybut
            if (service.isGraphical) {
                // Dla usług graficznych sprawdź packageTestPath
                setServiceHasNoTestPath(!service.packageTestPath);
            } else {
                // Dla usług niegraficznych sprawdź questionsJson
                setServiceHasNoTestPath(!service.questionsJson);
            }
        } else {
            // Brak wybranej usługi
            setServiceHasNoTestPath(false);
        }
    }, [selectedServiceId, possibleServices]);

    const paymentOrderStateLabels = {
        [PaymentOrderState.PENDING]: 'Oczekujące',
        [PaymentOrderState.WAITING_FOR_CONFIRMATION]: 'Oczekuje na potwierdzenie',
        [PaymentOrderState.COMPLETED]: 'Zakończone',
        [PaymentOrderState.CANCELED]: 'Anulowane',
    };

    const paymentOrderStateOptions = Object.entries(PaymentOrderState)
        .filter(([, value]) => typeof value === 'number')
        .map(([, value]) => ({ label: paymentOrderStateLabels[value as PaymentOrderState], value: value as number }));

    const isPathAdminOrders = location.pathname.startsWith('/admin/orders');

    const handleGenerateAIEffects = async () => {
        if (!customExamName || !trainingProgram) {
            toast.warning('Wypełnij nazwę egzaminu i program szkolenia przed generowaniem efektów.');
            return;
        }

        // Cancel any previous ongoing request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        
        // Create new AbortController for this request
        abortControllerRef.current = new AbortController();

        setGeneratingEffects(true);
        try {
            const response = await agent.AI.generateEducationEffects(
                {
                    customExamName: customExamName.trim(),
                    trainingProgram: trainingProgram.trim()
                },
                abortControllerRef.current.signal
            );

            // Check if component is still mounted before updating state
            if (!isMountedRef.current) {
                return;
            }

            if (response.effects && response.effects.length > 0) {
                // Join effects with semicolon and set to form
                const effectsString = response.effects.join(';');
                setValue('educationEffects', effectsString, { shouldDirty: true });
                toast.success(`Wygenerowano ${response.effects.length} efektów kształcenia. Możesz je teraz przejrzeć i edytować.`);
                // Open modal to allow user to review/edit
                handleOpenModal();
            } else {
                toast.warning('Nie udało się wygenerować efektów kształcenia. Spróbuj ponownie.');
            }
        } catch (error: any) {
            // Don't show error if request was cancelled (user navigated away)
            if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
                console.log('AI generation request was cancelled');
                return;
            }
            
            // Check if component is still mounted before showing error
            if (!isMountedRef.current) {
                return;
            }
            
            console.error('Error generating education effects:', error);
            const errorMessage = error?.data?.error || error?.message || 'Wystąpił błąd podczas generowania efektów kształcenia. Spróbuj ponownie.';
            toast.error(errorMessage);
        } finally {
            // Only update state if component is still mounted
            if (isMountedRef.current) {
                setGeneratingEffects(false);
            }
        }
    };

    return (
        <>
            <Typography variant="h5" sx={{ mt: 2, mb: 2 }}>Podstawowe dane o egzaminie</Typography>
            <Grid container spacing={2} marginBottom={3}>
                {order && (
                    <>
                        <Grid item xs={12}>
                            <AppSelect<OrderFormValues>
                                control={control}
                                name='paymentOrderState'
                                label='Stan płatności'
                                items={paymentOrderStateOptions}
                                disabled={!isPathAdminOrders}
                            />
                        </Grid>
                        {isAdmin && ((order.isGraphical && !order.packageTestPath) || (!order.isGraphical && !order.questionsJson)) && (
                            <Grid item xs={12}>
                                <Alert severity="error">
                                    {'Brak testów egzaminacyjnych w usłudze "'}<a href={'/services/' + order.serviceId}>{order.serviceName}</a>." Brak możliwości udostępnienia testów klientowi.
                                </Alert>
                            </Grid>
                        )}
                        <Grid item xs={6}>
                            <Typography variant="h6">Nr zamówienia: {order.publicId}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="h6">Zamawiający: {order.userDisplayName}</Typography>
                        </Grid>
                    </>
                )}
                <Grid item xs={12}>
                    <AppAutocompleteWithDraft<OrderFormValues>
                        control={control}
                        name='serviceId'
                        label='Wskaż egzamin'
                        items={possibleServices}
                        selectedids={order?.serviceId.toString()}
                        onChange={setManualServiceChange}
                    />
                </Grid>
                {serviceHasNoTestPath && isAdmin && (
                    <Grid item xs={12} sx={{ mb: 1 }}>
                        <Alert severity="warning">
                            {selectedService && selectedService.isGraphical
                                ? "Wybrana usługa nie posiada wzoru arkusza egzaminacyjnego. Udostępnienie testów będzie możliwe po dodaniu arkusza do odpowiedniej usługi."
                                : "Wybrana usługa nie posiada pytań egzaminacyjnych. Udostępnienie testów będzie możliwe po dodaniu pytań do odpowiedniej usługi."}
                        </Alert>
                    </Grid>
                )}
            </Grid>

            <Grid container spacing={3} marginBottom={3}>
                <Grid item xs={12}>
                    <AppTextInput control={control} name='customeClientServiceName' label='Nazwa egzaminu, jaka ma pojawić się na certyfikatach i protokołach' />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <AppDatePicker control={control} name='preposedDateTime' label='Proponowana data realizacji' disabled={false} />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <AppTextInput control={control} name='placeOfServiceAndAnotherInformations' label='Miejsce realizacji (ulica, nr budynku/lokalu, miejscowość)' />
                </Grid>
                <Grid item xs={12}>
                    <AppTextInput control={control} name='clientComment' label='Twój komentarz/uwagi' />
                </Grid>
                <Grid item xs={12} sm={12}>
                    <AppTextInput control={control} name='trainingProgram' label='Program szkolenia' multiline rows={3} />
                </Grid>
                {canGenerateAIEffects && (
                    <Grid item xs={12}>
                        <Alert 
                            severity="info" 
                            icon={<AutoAwesomeIcon />}
                            sx={{ 
                                '& .MuiAlert-message': { 
                                    display: 'flex', 
                                    flexDirection: 'column', 
                                    gap: 1,
                                    width: '100%'
                                } 
                            }}
                        >
                            <Box>
                                <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                                    Wykryto niestandardową nazwę egzaminu i program szkolenia.
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1.5 }}>
                                    Możesz automatycznie wygenerować 15 efektów kształcenia dopasowanych do Twojego programu szkolenia przy pomocy sztucznej inteligencji. 
                                    Po wygenerowaniu będziesz mógł je przejrzeć, edytować lub usunąć poszczególne punkty.
                                </Typography>
                                <LoadingButton
                                    variant="contained"
                                    color="primary"
                                    size="small"
                                    loading={generatingEffects}
                                    loadingPosition="start"
                                    startIcon={<AutoAwesomeIcon />}
                                    onClick={handleGenerateAIEffects}
                                >
                                    {generatingEffects ? 'Generowanie...' : 'Wygeneruj efekty AI'}
                                </LoadingButton>
                            </Box>
                        </Alert>
                    </Grid>
                )}
                <Grid item xs={12}>
                    <Alert severity="info">Edytuj poniższe pole w sytuacji, gdy oczekujesz istotnie odmiennego opisu efektów kształcenia, niż widniejące we wzorze</Alert>
                </Grid>
                <Grid item xs={12} sm={12}>
                    <Typography variant="h5" sx={{ mt: 2 }}>Efekty kształcenia:</Typography>
                </Grid>
                <Grid item xs={12} sm={12}>
                    {watch('educationEffects') ? watch('educationEffects')?.split(';').map((effect: string, index: number) => (
                        <Typography key={index}>{index + 1}. {effect}</Typography>
                    )) : null}
                </Grid>
                <Grid item xs={12} sm={12}>
                    <Button variant="outlined" onClick={handleOpenModal}>Edytuj Efekty Kształcenia</Button>
                </Grid>
            </Grid>
        </>
    );
}